'use client';

import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, RotateCcw, Save, TriangleAlert, X } from 'lucide-react';
import { api, getApiErrorMessage } from '@/lib/api';
import { TERMS, aMissionType, plural } from '@/lib/copy';

import { Button } from '@/components/ui/button';
import { usePointerDrag } from '@/hooks/usePointerDrag';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { STATUS_BADGE_MAP } from '../missions/_components/missionListShared';
import {
  draftRouteDistanceM,
  draftToWaypointPayload,
  effectiveWaypointAltitude,
  type DraftWaypoint,
} from '../missions/_components/missionCanvasShared';
import {
  MISSION_TYPE_LABELS,
  waypointCountRangeForType,
  type MissionType,
} from '@/lib/mission-type';
import { clampDockSheetWidth } from './CopDockSheet';
import { RouteWaypointList } from './RouteWaypointList';
import type { DrawnRouteController } from './useDrawnRoute';

// ---------------------------------------------------------------------------
// Revising a saved mission's route on the live map.
//
// THE LOCK RULE, which is not negotiable: an aircraft flying a route is flying
// the route the server handed it, and nothing on any surface may edit that
// route in place. `in_progress` never reaches this panel — the queue offers no
// edit control for it and says why — and if it somehow did, the server refuses
// (`PUT /api/missions/:id/waypoints` → 409 `route_not_editable`). The
// briefing and the after-action record are evidence; rewriting them to describe
// a route nobody flew is the same class of problem the append-only flight log
// exists to prevent.
//
// A SCHEDULED mission is a commitment somebody made, so editing it is an
// explicit act: the panel opens locked and the operator presses REVISE. A DRAFT
// is working paper and opens ready to edit.
//
// DIRTY STATE IS ALWAYS VISIBLE. It must never be ambiguous what the aircraft
// would actually fly: the panel says "unsaved" the moment the drawn route
// differs from the stored one, and closing with changes asks first.
// ---------------------------------------------------------------------------

/**
 * Stable signature of a DRAWN route, for comparing what is on the map against what is stored.
 *
 * Named apart from `missionDetailShared.routeSignature` deliberately: that one signs the server's
 * `Waypoint` rows including speed, action and hover, and this one signs `DraftWaypoint`s at their
 * effective altitude. They answer "has the route changed" differently — each correctly, for its
 * own input — so a shared name was an invitation to compute dirtiness with the wrong one and get
 * a verdict that quietly disagrees with what a save would write.
 *
 * Signs the ALTITUDE too, at each point's effective value — which is why the mission altitude has
 * to be passed in. Signing position alone was already wrong the moment a waypoint could carry its
 * own altitude: an operator who raised WP7 from 50 m to 300 m and changed nothing else would read
 * "Matches the saved route" over a route the aircraft has never been given, and this panel's one
 * rule is that dirty state is always visible.
 *
 * Effective rather than raw, so a point that follows the mission altitude and one explicitly set
 * to the same number sign the same: they would be saved as the same row, and a dirty mark for a
 * save that would change nothing is noise of the opposite kind.
 */
/** Ceiling on the route save. Ordinary CRUD — no dock hardware waits behind it. */
const EDIT_SAVE_TIMEOUT_MS = 10_000;

export function drawnRouteSignature(
  waypoints: ReadonlyArray<DraftWaypoint>,
  missionAltitudeM: number,
): string {
  return waypoints
    .map((wp) => `${wp.lat.toFixed(6)},${wp.lng.toFixed(6)},${effectiveWaypointAltitude(wp, missionAltitudeM)}`)
    .join('|');
}

export interface EditingMission {
  id: string;
  name: string;
  status: string;
  /**
   * 022. Carried so the map can be armed for the right GEOMETRY when this route is revised —
   * an orbit collects one centre point, everything else an ordered list — and so the save
   * blocker can state the right minimum.
   */
  type: MissionType;
  /**
   * The mission's default altitude. Points the operator has not given an altitude of their own
   * fly at this; stored points carry their own, and a save preserves them.
   */
  altitudeM: number;
  /** Signature of the route as the server holds it. */
  storedSignature: string;
}

export interface CopMissionEditProps {
  mission: EditingMission;
  route: DrawnRouteController;
  /** True while the map is armed for this edit — the operator can place points. */
  armed: boolean;
  onArm: () => void;
  onDisarm: () => void;
  /** Route saved — the caller refreshes the queue and re-reads the stored signature. */
  onSaved: (missionId: string, waypoints: DraftWaypoint[]) => void;
  /** Put the stored route back on the map (revert). */
  onRevert: () => void;
  onClose: () => void;
  width: number;
  onWidthChange: (width: number) => void;
}

export function CopMissionEdit({
  mission,
  route,
  armed,
  onArm,
  onDisarm,
  onSaved,
  onRevert,
  onClose,
  width,
  onWidthChange,
}: CopMissionEditProps) {
  const [saving, setSaving] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const startWidth = useRef(width);
  const { onPointerDown: onResizePointerDown } = usePointerDrag({
    cursor: 'ew-resize',
    onDragStart: () => {
      startWidth.current = width;
    },
    onDrag: ({ dx }) => onWidthChange(clampDockSheetWidth(startWidth.current - dx, window.innerWidth)),
  });

  const { waypoints, selectedIndex, violations, invalidIndices } = route;
  const distanceM = useMemo(() => draftRouteDistanceM(waypoints), [waypoints]);
  const dirty = drawnRouteSignature(waypoints, mission.altitudeM) !== mission.storedSignature;
  const firstError = violations.find((v) => v.severity === 'error') ?? null;

  /**
   * A route the API will not store, so it must never be offered as a save.
   *
   * 022: the bounds are the TYPE's, not a flat 2..100. A GO TO POINT and an ORBIT each store
   * exactly one point, and a HOVER or an OUT & BACK may store one, so the flat minimum made
   * those types impossible to save at all — the blocker would demand a second point the API
   * does not require.
   */
  const routeBounds = waypointCountRangeForType(mission.type);
  const saveBlocker = waypoints.length < routeBounds.min
    ? routeBounds.min === 1
      ? `${aMissionType(mission.type, { capitalised: true })} needs its point placed`
      : `A route needs at least ${routeBounds.min} points`
    : waypoints.length > routeBounds.max
      ? routeBounds.max === 1
        ? `${aMissionType(mission.type, { capitalised: true })} takes one point, not ${waypoints.length}`
        : `${waypoints.length} points exceeds the ${routeBounds.max} limit`
      : null;

  const handleSave = async () => {
    if (saveBlocker) {
      toast.error(saveBlocker);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/missions/${mission.id}/waypoints`, {
        waypoints: draftToWaypointPayload(waypoints, mission.altitudeM),
      },
        // Bounded like every other write in this flow: this one holds the REVISED route, which
        // lives nowhere but `useDrawnRoute` state, and a hang leaves Save/Revert/Done disabled
        // behind "Saving…" with only a reload as a way out — which throws the revision away.
        { signal: AbortSignal.timeout(EDIT_SAVE_TIMEOUT_MS) },
      );
      toast.success(`Route saved, ${plural(waypoints.length, TERMS.waypoint, TERMS.waypoints)}`);
      onSaved(mission.id, waypoints);
      // Point entry stands down on a save: the saved state is the resting state,
      // and leaving the map armed invites a click that re-dirties it unnoticed.
      onDisarm();
    } catch (err) {
      // The server's own words. A 409 here means the mission moved on — most
      // likely it launched while the operator was drawing.
      toast.error(getApiErrorMessage(err, 'Failed to save the route'));
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) {
      setConfirmLeave(true);
      return;
    }
    onClose();
  };

  return (
    <div
      /* `overflow-clip`, never `overflow-hidden` — the same rule as `CopMissionCompose`, and for
         the same reason. `hidden` is a scroll container with no scrollbar and no wheel response:
         the browser can scroll it, the operator cannot scroll it back. `clip` clips the same and
         is not scrollable at all, so this panel can never be parked somewhere with no way out.
         This one has no readiness rail, so it was not reachable here in practice — but the
         waypoint list below can run to 100 points, and the trap is in the box, not the content. */
      className={`
        flex flex-col min-h-0 overflow-clip
        lg:w-[var(--dock-sheet-width)] lg:shrink-0 lg:relative lg:z-auto lg:h-auto
        fixed bottom-0 left-0 right-0 z-40 max-w-[100vw]
        rounded-t-xl border-t border-subtle bg-popover/90 backdrop-blur-sm
        shadow-sheet
        lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none lg:backdrop-blur-none
      `}
    >
      <button
        type="button"
        aria-label="Resize route editor"
        onPointerDown={onResizePointerDown}
        className="hidden lg:block absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize border-0 bg-transparent p-0 hover:bg-status-blue/30 focus:outline-none focus-visible:bg-primary"
      />

      <div className="flex-1 min-h-0 flex flex-col max-h-[min(70vh,44rem)] lg:max-h-full bg-card/60">
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b divider shrink-0">
          <span className="text-[11px] uppercase tracking-wider text-foreground truncate">
            Edit route
          </span>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close route editor"
            className="p-1.5 -my-0.5 -mx-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="size-4" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        {/* `relative` because this scrolls: a scroll container that is not a containing block
            lets absolutely positioned descendants escape its clip and sit at their un-scrolled
            static position, projecting overflow onto whatever positioned ancestor is above. */}
        <div className="relative flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
          <div className="flex items-center gap-2 min-w-0">
            <StatusBadge status={STATUS_BADGE_MAP[mission.status] || 'offline'} />
            <span className="flex-1 min-w-0 truncate text-xs font-medium text-foreground">{mission.name}</span>
          </div>

          {/* Never ambiguous what the aircraft would actually fly. */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-foreground tabular-nums">
              {waypoints.length} WP · {(distanceM / 1000).toFixed(2)} km
              {/* #373. Which point it stops over — the one thing that distinguishes this
                  mission from a FOLLOW ROUTE with the same markers on the map, and the one
                  thing revising the route can silently change: on a HOVER a map click now
                  APPENDS, so a click meant to reposition the hold adds a point and the hold
                  moves with it. Named here, the number moves in front of the operator. */}
              {mission.type === 'hover' && waypoints.length > 1 && (
                <span className="text-muted-foreground"> · holds at WP{waypoints.length}</span>
              )}
            </span>
            {dirty ? (
              <span className="rounded border border-status-amber/40 bg-status-amber/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-status-amber-fg">
                Unsaved
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Matches the saved route
              </span>
            )}
          </div>

          {/* A commitment somebody made. Editing it is an explicit act, not a
              side effect of selecting it. A draft is working paper — no gate. */}
          {!armed ? (
            <div className="space-y-2 rounded border border-subtle bg-background/40 p-2.5">
              <p className="text-[11px] text-muted-foreground">
                {mission.status === 'scheduled'
                  ? 'This mission is scheduled. Revise its route to change what will be flown.'
                  : 'Revise the route to place or move waypoints on the map.'}
              </p>
              <Button
                type="button" size="sm" onClick={onArm} disabled={saving}
                className="h-7 px-2.5 text-[10px] uppercase tracking-wider bg-button-primary hover:bg-button-primary-hover text-primary-foreground"
              >
                <Pencil className="size-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
                Revise route
              </Button>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Click the map to add a point, drag one to move it. Double-click or Esc stops placing.
            </p>
          )}

          {saveBlocker && (
            <p className="flex items-start gap-1.5 text-[11px] text-status-amber-fg">
              <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
              {saveBlocker}
            </p>
          )}
          {firstError && (
            <p className="flex items-start gap-1.5 text-[11px] text-status-red-fg" role="alert">
              <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
              {firstError.message}
            </p>
          )}

          {/* Locked means locked: with the map disarmed, the route is readable but
              nothing about it — position or altitude — can be changed here. */}
          {waypoints.length > 0 && armed && (
            <p className="text-[10px] text-muted-foreground">Blank altitude follows the mission&apos;s {mission.altitudeM} m.</p>
          )}
          <RouteWaypointList
            waypoints={waypoints}
            selectedIndex={selectedIndex}
            invalidIndices={invalidIndices}
            missionAltitudeM={mission.altitudeM}
            onSelect={route.select}
            onAltitudeChange={armed ? route.setAltitudeAt : undefined}
            onRemove={armed ? route.removeAt : undefined}
            disabled={saving}
            className="max-h-48"
          />
        </div>

        <div className="shrink-0 border-t divider px-3 py-2 bg-card/80">
          <p role="status" aria-live="polite" className="sr-only">
            {dirty ? 'The drawn route differs from the saved route.' : 'The drawn route matches the saved route.'}
          </p>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {confirmLeave ? (
              <>
                <span className="text-[11px] text-status-amber-fg">Leave? The changes are not saved.</span>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setConfirmLeave(false)}
                  className="border-border text-muted-foreground"
                >
                  Keep editing
                </Button>
                <Button
                  type="button" size="sm" onClick={onClose}
                  className="bg-button-destructive hover:bg-button-destructive-hover text-destructive-foreground"
                >
                  Discard changes
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button" variant="outline" size="sm" disabled={saving || !dirty}
                  onClick={onRevert}
                  className="border-border text-muted-foreground hover:bg-accent"
                >
                  <RotateCcw className="size-3.5 mr-1.5" strokeWidth={1.5} aria-hidden="true" />
                  Revert
                </Button>
                <Button
                  type="button" variant="outline" size="sm" disabled={saving}
                  onClick={requestClose}
                  className="border-border text-muted-foreground hover:bg-accent"
                >
                  Done
                </Button>
                <Button
                  type="button" size="sm" disabled={saving || !dirty || saveBlocker !== null}
                  onClick={() => void handleSave()}
                  className="bg-button-primary hover:bg-button-primary-hover text-primary-foreground"
                >
                  <Save className="size-4 mr-1.5" strokeWidth={1.5} aria-hidden="true" />
                  {saving ? 'Saving…' : 'Save route'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
